import React from 'react';
import { SearchProps } from '../../../types/DesignSystem/Search';
import { FieldMessages, fieldInvalid, messageIds } from '../fieldHelper';
import { useControllableState } from '../useControllableState';
import FieldTitle from '../FieldTitle/FieldTitle';
import Spinner from '../Spinner/Spinner';
import Kbd, { keysOf } from '../Kbd/Kbd';
import { FOCUS_RING } from '../focusRing';
import { FIELD_BOX, fieldBorder, FIELD_REST_WITHIN, FIELD_DISABLED } from '../fieldChrome';
import { CONTROL_HEIGHT, FIELD_INSET, resolveControlSize } from '../controlSize';
import { Cross, Search as SearchGlyph } from '../Iconography/Icons';
import { useDebouncedCallback } from '../debounce';
import { useControlSize } from '../DensityProvider/DensityProvider';
import { withClassName } from '../cn';

const Search = React.forwardRef<HTMLInputElement, SearchProps>(function Search({
  label,
  labelSize,
  placeholder = 'Search...',
  value: valueProp,
  defaultValue,
  onChange,
  onSearch,
  loading = false,
  loadingLabel = 'Searching',
  disabled = false,
  clearable = true,
  size: sizeProp,
  variant = 'default',
  className = '',
  helperText,
  error = false,
  errorMessage,
  hideLabel = false,
  searchIconPosition = "start",
  shortcut,
  debounceMs,
  'data-testid': dataTestId,
  ariaLabel,
  'aria-label': ariaLabelNative,
  'aria-describedby': describedByProp,
  onKeyDown: onKeyDownProp,
  ...rest
}, ref) {
  /*
    The helper text's id was the literal string `search-helper`. Two Search
    components on one page therefore emitted the same DOM id, and every one of
    their inputs pointed `aria-describedby` at whichever came first — so the
    second field's helper text, including its error text, was never announced.
    `useId` is React's answer to exactly this and the design system was not using
    it anywhere.
  */
  const uid = React.useId();
  const inputId = `${uid}-input`;
  const helperId = `${uid}-helper`;
  const errorId = `${uid}-error`;
  /* § 356 / § 357 — see TextField. */
  const invalid = fieldInvalid(error, errorMessage);
  const [value, setValue] = useControllableState(valueProp, defaultValue, '');
  /*
    Padding only. These carried `tw-text-sm` / `tw-text-base` / `tw-text-xl` — 14 /
    16 / 18px off the RAW scale rather than the design system's semantic tokens — so
    the default Search rendered its text at 16px while every other control renders
    at `body-primary`'s 14px, and `large` reached 18px, which is a heading size here.
    Size is density; the type token is fixed.
  */
  /**
   * Height and inset come from the shared control grid.
   *
   * **What this replaced, and why both halves were wrong.** The horizontal padding used to
   * sit on the *input* rather than the row, so it padded the text and not the field: the
   * icon sat flush against the border with only a `tw-pl-1` on the glyph holding it off —
   * measured, 4px from the border to the icon and 20px from the icon to the placeholder.
   * Moving it to the row fixed that. The *vertical* half then stayed as `py-1/2/3` on the
   * input, which meant the row's height was arithmetic — `py-2` + a 20px line box + a 2px
   * border = **38px** — against a `Button`'s declared 32. Two controls in one toolbar,
   * 6px apart, and neither number chosen.
   *
   * Now the row declares its height and its inset, and the input just fills it. One value
   * produces the whole thing: border → icon, text → right border, `gap-2` between.
   *
   * `medium` maps to `md` (36px) and keeps `px-3`, the same inset `TextField` uses, so a
   * Search and a TextField stacked in a form still line up — which was the point of the
   * original fix and is now true of their heights as well.
   */
  /* § 313 — the region's density decides when the caller did not. */
  const size = useControlSize(sizeProp);

  /*
    `onKeyDown`, not `onKeyPress`: the latter is deprecated in React and this was the
    only thing carrying Enter-to-search. Composed rather than replaced — rest-first
    spreading protects this handler from a caller's, which would otherwise have
    dropped theirs silently.
  */
  /*
    § 303 — one debounced channel to `onSearch`. `call` is the typing path and fires
    only when `debounceMs` is set; `now` is Enter and the clear ×, which fire at once
    and cancel anything pending — so a query typed and then submitted reaches the
    caller exactly once. With `debounceMs` unset the typing path never fires and the
    other two are the synchronous calls this component always made.
  */
  const search = useDebouncedCallback<[string]>(onSearch, debounceMs);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      search.now(value);
    }
    if (!e.defaultPrevented) onKeyDownProp?.(e);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    onChange?.(e.target.value, e);
    if (debounceMs) search.call(e.target.value);
  };

  const handleClear = () => {
    setValue('');
    onChange?.('');
    search.now('');
  };

  const searchIcon = (
    /* § 232 — the set's own magnifier, not a private 24-grid/stroke-2 drawing: the
       Iconography `Search` glyph could take no props until now, which is the only
       reason this component ever drew its own. `aria-hidden` — the input names the
       field; the glyph decorates it. */
    /* grey-600 (4.52:1 on white), not grey-500 (2.42:1 on the old tinted ground).
       SC 1.4.11 wants 3:1 of a graphical object that carries meaning, and once the
       placeholder is typed over this glyph is the only thing marking the field as
       a search. */
    /* No `tw-pl-1`/`tw-pr-1` here any more. That was standing in for the row padding
       that did not exist, and it shifted the glyph inside its own 20px box rather than
       moving the box — so the icon and the text were inset by different amounts. */
    <SearchGlyph
      size={20}
      aria-hidden="true"
      className={`tw-flex-shrink-0 ${invalid ? 'tw-text-tone-danger-ink' : disabled ? 'tw-text-ink-disabled' : 'tw-text-ink-tertiary'}`}
    />
  );

  /*
    § 288 — the spinner TAKES the glyph's slot, the same move § 209 made in `Button`,
    and for the same reason: the row's child count does not change, so a query
    starting reflows nothing. It follows `searchIconPosition` because the slot it is
    borrowing does.

    The 20px box is the glyph's own size — `Spinner size="sm"` draws at 16 — so the
    inset the row owns (§ 39) measures exactly what it did before. Rendering the
    spinner bare would have moved the text 4px on every keystroke's fetch.

    It is NOT suppressed while `disabled`: a caller passing both has said the field is
    locked *and* working, and a wait that disappears when you lock the field is a lie.
    The row's opacity dims it along with everything else, which is the honest reading.
  */
  const busyIcon = (
    <div className="tw-w-5 tw-h-5 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
      <Spinner
        size="sm"
        label={loadingLabel}
        data-testid={dataTestId ? `${dataTestId}-spinner` : undefined}
      />
    </div>
  );

  /* One slot, two possible occupants — resolved once so `start` and `end` can never
     disagree about which is showing. */
  const fieldGlyph = loading ? busyIcon : searchIcon;

  return (
    <div className={withClassName(`tw-flex tw-flex-col tw-gap-2`, className)}>
      {label && (
        /* § 355 — hidden, not removed: it stays the input's accessible name. Out of flow
           while hidden, so the column's gap does not open under it. */
        <FieldTitle
          label={label}
          size={labelSize}
          htmlFor={inputId}
          disabled={disabled}
          className={hideLabel ? 'tw-sr-only' : undefined}
        />
      )}
      <div
        /*
          § 302 — the deprecated `variant` leaves a MARKER, and only for the deprecated
          value. The prop has been inert since the § 29 unification (both dialects render
          identically) and the primitive-contract scan had counted it as "read" only
          because the word `variant` appeared on the helper's Typography — which now
          lives in `fieldHelper.tsx`. Four call sites still pass `bordered`; a spec or a
          sweep can find them by `[data-variant="bordered"]` and retire the dialect. The
          default emits nothing, so every other Search is byte-identical.
        */
        data-variant={variant === 'bordered' ? 'bordered' : undefined}
        /*
          Three changes here, each measured.

          **Error is border-only, in `red-error-900`.** It was a `red-error-500`
          border over a `red-error-100` fill — a whole pink block — while `TextField`
          across 36 call sites changes only its border, in `red-error-900`. Two error
          idioms in one design system, and the louder one competed with the error text
          sitting directly beneath it.

          **The ground is white in both variants.** The `default` variant tinted the
          field `grey-100`, and `grey-600` placeholder text on `grey-100` is 4.14:1 —
          under AA — where on white it is 4.52:1. The tint was the only thing breaking
          it. `variant` is consequently inert and marked deprecated.

          **Focus is a ring, not just a colour swap.** `blue-500` against the resting
          `grey-550` border measures 1.77:1; against white the ring is 5.62:1. The
          input's bare `tw-outline-none` had been removing the browser default that
          was quietly doing this job.
        */
        className={`tw-flex tw-items-center tw-gap-2 ${FIELD_BOX} ${
          CONTROL_HEIGHT[size]
        } ${FIELD_INSET[size]} ${fieldBorder({ error: invalid, disabled })} ${disabled
            /* § 296 — disabled recedes BY TOKEN, the same `FIELD_DISABLED` every
               other field wears. This was `tw-opacity-50` over a white row, "so the
               whole row dims as one" — the last opacity recede in the system after
               § 123 (Toggle), § 227 (Checkbox, Radio) and § 228 (Menu) each removed
               theirs: the token IS the recede, opacity only multiplies it. Washed
               ink computed to 3.56:1 light / 4.35:1 dark where the system's disabled
               ink measures 2.47 / 2.70, so a disabled Search read LESS disabled than
               the disabled TextField beside it, and its border and glyph faded with
               it instead of receding to `edge` and `ink-disabled`. The glyph and the
               input's ink take the token below. The hover whisper lives in the
               ENABLED branch only: a disabled field that still reacted would be
               advertising itself as live. */
            ? FIELD_DISABLED
            : FIELD_REST_WITHIN
          }`}
      >
        {searchIconPosition === 'start' && fieldGlyph}

        <input
          /* rest first — see the rule in Button.tsx. */
          {...rest}
          ref={ref}
          id={inputId}
          /* `type="search"` gives the role `searchbox` rather than a plain
             `textbox`. WebKit then draws its own clear affordance, which would sit
             beside this component's — hence hiding it. */
          type="search"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className={
            /* `tw-h-full`, not a `py-*` map: the ROW owns the height, and the input
               stretching to fill it keeps the whole field clickable — with padding on
               the input instead, a click near the top or bottom edge landed on nothing. */
            'tw-flex-1 tw-min-w-0 tw-h-full tw-bg-transparent tw-outline-none tw-text-body-primary '
            /* § 296 — the input carries its own ink, so the row's `FIELD_DISABLED`
               ink cannot reach it; the disabled pair is chosen here, never stacked. */
            + (disabled
              ? 'tw-text-ink-disabled placeholder:tw-text-ink-disabled '
              : 'tw-text-ink placeholder:tw-text-ink-secondary ')
            + 'disabled:tw-cursor-not-allowed [&::-webkit-search-cancel-button]:tw-hidden'
          }
          aria-invalid={invalid || undefined}
          /* `|| undefined` so a field that is not searching carries no attribute at
             all, exactly as `aria-invalid` does here and `aria-busy` does on Button.
             `aria-busy="false"` on every search box in the app is noise a screen
             reader has to step over. Deliberately NOT paired with `disabled`: see
             `loading` in the props. */
          aria-busy={loading || undefined}
          /* § 297 — the announced half of `shortcut`; the caps below are its picture. */
          aria-keyshortcuts={shortcut || undefined}
          aria-describedby={
            [describedByProp, ...messageIds({ helperId, errorId }, { helperText, errorMessage })]
              .filter(Boolean).join(' ') || undefined
          }
          /* Only when there is no visible label to name it. A `label` names the input
             through `htmlFor`, and an `aria-label` alongside it would override that
             text rather than add to it (WCAG 2.5.3). */
          aria-label={ariaLabelNative ?? ariaLabel ?? (label ? undefined : placeholder)}
          data-testid={dataTestId}
        />

        {searchIconPosition === 'end' && fieldGlyph}

        {/* § 297 — the shortcut caps (Menu's § 247 arrangement): shown while there is
            nothing typed, so the trailing slot is either the hint or the clear button,
            never both; hidden while disabled, because a locked field that advertised a
            key would be lying. `aria-hidden` — `aria-keyshortcuts` on the input is the fact. */}
        {shortcut && !value && !disabled && (
          <span
            aria-hidden="true"
            className="tw-flex tw-items-center tw-gap-1 tw-shrink-0"
            data-testid={dataTestId ? `${dataTestId}-shortcut` : undefined}
          >
            {keysOf(shortcut).map((key, index) => (
              // eslint-disable-next-line react/no-array-index-key -- a chord can legitimately repeat a key
              <Kbd key={`${key}-${index}`}>{key}</Kbd>
            ))}
          </span>
        )}

        {clearable && value && !disabled && (
          <button data-testid={dataTestId ? `${dataTestId}-clear` : 'search-search-button-1'}
            onClick={handleClear}
            className={`tw-p-1 tw-rounded-ctl tw-text-ink-tertiary hover:tw-text-ink tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift tw-flex-shrink-0 ${FOCUS_RING}`}
            aria-label="Clear search"
            type="button"
          >
            {/* § 232 — the set's Cross at 16, the exact glyph and size Toast's and
                Alert's dismiss draw — this was a fourth private × (a filled path,
                the only non-stroked mark in the system). The p-1 keeps the 24px
                target (SC 2.5.8). */}
            <Cross size={16} aria-hidden="true" />
          </button>
        )}
      </div>

      {/* § 302 — the one helper seam. The id moves from a wrapping div onto the message
          itself (what `aria-describedby` resolves to is the same text), and an errored
          Search now announces (role="alert", remounted) like every other field. */}
      <FieldMessages
        helperId={helperId}
        errorId={errorId}
        helperText={helperText}
        errorMessage={errorMessage}
        error={error}
        data-testid={dataTestId}
      />
    </div>
  );
});

export default Search;
